/**
 * Deferred third-party loader.
 *
 * Every non-essential third-party script (Meta Pixel, GA4, LeadConnector
 * external tracking, LeadConnector chat widget) is injected only after the
 * page has painted — on the first user interaction, or on idle after the
 * `load` event, whichever comes first. Nothing here blocks the critical
 * rendering path, and each integration is guarded so it initializes once.
 */

const META_PIXEL_ID = "322880580380143";
const GA_MEASUREMENT_ID = "G-4XH07FLDMK";
const LC_TRACKING_ID = "tk_4e99c8482de74a3cba3ad9698262953d";
const LC_CHAT_WIDGET_ID = "6a54154ac6e06ac8e846e2ca";

type Win = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
  _fbq?: unknown;
  __thirdPartyLoaded?: boolean;
  __lcChatLoading?: boolean;
};

const w = () => window as unknown as Win;

function injectScript(src: string, attrs: Record<string, string> = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

/* ---------------------------------------------- Google Analytics (GA4) */

let gaReady = false;

function loadGA() {
  if (gaReady) return;
  gaReady = true;
  const win = w();
  win.dataLayer = win.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  win.gtag = function gtag() { win.dataLayer!.push(arguments); } as unknown as Win["gtag"];
  win.gtag!("js", new Date());
  // Route changes are reported explicitly via trackPageView().
  win.gtag!("config", GA_MEASUREMENT_ID, { send_page_view: true });
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`);
}

/* ---------------------------------------------------------- Meta Pixel */

let pixelReady = false;

function loadMetaPixel() {
  if (pixelReady) return;
  pixelReady = true;
  const win = w();
  if (win.fbq) return; // already initialized elsewhere — never init twice
  /* eslint-disable */
  (function (f: any, b: any, e: string, v: string) {
    let n: any, t: any, s: any;
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  win.fbq!("init", META_PIXEL_ID);
  win.fbq!("track", "PageView");
}

/* -------------------------------------- LeadConnector external tracking */

function loadLeadConnectorTracking() {
  injectScript("https://link.msgsndr.com/js/external-tracking.js", {
    "data-tracking-id": LC_TRACKING_ID,
  });
}

/* ------------------------------------------- LeadConnector chat widget */

/** Loads the real GHL chat widget on demand (used by the lightweight launcher). */
export function loadChatWidget(): Promise<void> {
  const win = w();
  if (win.__lcChatLoading) return Promise.resolve();
  win.__lcChatLoading = true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://widgets.leadconnectorhq.com/loader.js";
    s.async = true;
    s.setAttribute("data-resources-url", "https://widgets.leadconnectorhq.com/chat-widget/loader.js");
    s.setAttribute("data-widget-id", LC_CHAT_WIDGET_ID);
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.body.appendChild(s);
  });
}

export function isChatWidgetLoaded() {
  return !!w().__lcChatLoading;
}

/* ------------------------------------------------------- Orchestration */

function loadAll() {
  const win = w();
  if (win.__thirdPartyLoaded) return;
  win.__thirdPartyLoaded = true;
  loadGA();
  loadMetaPixel();
  loadLeadConnectorTracking();
}

const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll", "wheel"];

/** Call once at app startup. Schedules all deferred third-party scripts. */
export function initDeferredThirdParty() {
  if (typeof window === "undefined") return;

  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, run));
    loadAll();
  };

  INTERACTION_EVENTS.forEach((e) =>
    window.addEventListener(e, run, { once: true, passive: true }),
  );

  const schedule = () => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
      .requestIdleCallback;
    if (ric) ric(run, { timeout: 4000 });
    else window.setTimeout(run, 3000);
  };

  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}

/** Report an SPA route change to GA4 and Meta (no-op until they've loaded). */
export function trackPageView(path: string) {
  const win = w();
  win.gtag?.("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
  win.fbq?.("track", "PageView");
}
